import { ActivityInfo } from '@/modules/drivers/types/activities'
import React from 'react'
import Timeline from '../../../components_new/Timeline'
import { TimelineProps } from '../../../components_new/timelineProps'
import { SCENE_HEIGHT } from '../../../components_new/utils/layout'
import moment from 'moment'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { LoaderIcon } from '@/app/features/icons/Loader'
import { useLoadingStore, selectLoading } from '@/modules/shared/stores/loadingStore'
const DetailsChartWrapper = ({
  data,
  currentDate,
}: {
  data: ActivityInfo
  currentDate: string
}) => {
  const { t } = useLocalize()
  const [chartWidth, setChartWidth] = React.useState(0)
  const [chartHeight, setChartHeight] = React.useState(SCENE_HEIGHT)
  const loading = useLoadingStore(selectLoading)
  const wrapperRef = React.useRef<HTMLDivElement>(null)
  const [hoveredActivity, setHoveredActivity] = React.useState(null)

  const formatedDate = moment(currentDate)
  const activities = data?.activities || []

  const filteredActivities = activities.filter((activity) => {
    const dayStart = formatedDate.clone()
    const dayEnd = formatedDate.clone().add(1, 'day')
    const activityEnd = activity.endTime
      ? moment(activity.endTime)
      : moment(formatedDate).endOf('day')

    return (
      moment(activity.startTime).isBefore(dayEnd) &&
      activityEnd.isAfter(dayStart)
    )
  })

  const filteredCardActivities = filteredActivities.filter(
    (activity) =>
      !activity.isManualEntry &&
      !activity.isVehicleSource &&
      !activity.isModifiedByUser,
  )

  const filteredVehicleActivities = filteredActivities.filter(
    (activity) => activity.isVehicleSource === true,
  )

  const filteredModifiedActivities = filteredActivities.filter(
    (activity) =>
      activity.isManualEntry === true || activity.isModifiedByUser === true,
  )

  React.useEffect(() => {
    const measure = () => {
      if (!wrapperRef.current) return
      setChartWidth(wrapperRef.current.offsetWidth)
      const top = wrapperRef.current.getBoundingClientRect().top
      const SECTION_LABEL = 36
      const GAP = 8 
      const BOTTOM = 12
      const available = window.innerHeight - top - BOTTOM
      const perChart = Math.floor(
        (available - GAP * 3 - SECTION_LABEL * 4) / 4,
      )
      setChartHeight(Math.max(SCENE_HEIGHT, perChart))
    }
    measure()

    const resizeObserver = new ResizeObserver(measure)
    if (wrapperRef.current) {
      resizeObserver.observe(wrapperRef.current)
    }
    window.addEventListener('resize', measure)
    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [data, currentDate])
  const handleActivityHoverFromChart = (activity: any) => {
    setHoveredActivity(activity)
  }


  const buildTimelineProps = (sectionActivities: any[], full: boolean) =>
    ({
      startTime: formatedDate,
      days: 1,
      width: chartWidth,
      height: chartHeight,
      extraCurrentDate: currentDate,
      translate: t,
      activities: sectionActivities,
      places: full ? data?.places || [] : [],
      cardInserts: full ? data?.cardInserts || [] : [],
      dayActivities: full ? data?.dayActivities || [] : [],
      violations: full ? data?.violations || [] : [],
      dailyRests: full ? data?.dailyRests || [] : [],
      dailyPartialRegularRests: full ? data?.dailyPartialRegularRests || [] : [],
      weeklyRests: full ? data?.weeklyRests || [] : [],
      weeklyReducedRests: full ? data?.weeklyReducedRests || [] : [],
      breaks: full ? data?.breaks || [] : [],
      partialBreaks: full ? data?.partialBreaks || [] : [],
      weekRecompenses: full ? data?.weekRecompenses || [] : [],
      outOfScopes: full ? data?.outOfScopes || [] : [],
      existingDelegations: [],
      changeAreas: [],
      tmsData: [],
      minimalPaymentCountries: [],
      periodSelections: [],
      delegationMarkers: [],
      allEvents: [],
      allTripEvents: [],
      allPreviousTripEvents: [],
      countries: [],
      canEdit: true,
      canGenerateAttestation: false,
      selectedActivity: hoveredActivity,
      editedActivity: null,
      isAddingNew: false,
      flattenChart: false,
      simpleMode: false,
      disableTooltips: false,
      showViolations: true,
      showTrips: false,
      showDelegations: false,
      showRests: true,
      showRestParts: true,
      showDayActivities: true,
      editMode: false,
      onSelected: handleActivityHoverFromChart,
      onContextMenuChanged: () => {},
      onViolationSelected: () => {},
      onPeriodSelectionsChanged: () => {},
    }) as unknown as TimelineProps

  const LoadingState = () => (
    <div className="tw-top-12 tw-z-50 tw-flex tw-h-[150px] tw-w-full tw-items-center tw-justify-center">
      <LoaderIcon className="tw-w-16 tw-animate-spin" />
    </div>
  )
  const NoDataAvailable = ({ message }: { message?: string }) => (
    <div className="tw-absolute tw-left-1/2 tw-top-14 tw-z-50 -tw-translate-x-1/2 tw-text-gray-400">
      {message || 'Brak danych z tego okresu'}
    </div>
  )

  const hasNoData = !data || !currentDate
  const noDriverOrDate = !currentDate

  return (
    <div
      ref={wrapperRef}
      className="tw-flex tw-flex-col tw-gap-2 tw-bg-white"
    >
      <div className="tw-flex tw-flex-1 tw-flex-col tw-overflow-hidden">
        <div className="tw-relative tw-flex tw-flex-col tw-pt-7 ">
          <div className="tw-absolute tw-top-0 tw-z-[200] tw-pl-5 tw-pb-2">
            {t('driverModule.activities-from-card')}
          </div>
          {!loading && (hasNoData || filteredCardActivities.length === 0) && (
            <NoDataAvailable
              message={
                hasNoData ? t('driverModule.select-driver-and-date') : undefined
              }
            />
          )}
          {loading ? (
            <LoadingState />
          ) : (
            <Timeline
              key={`details-card-${formatedDate.toISOString()}`}
              {...buildTimelineProps(filteredCardActivities, true)}
            />
          )}
        </div>
      </div>
      <div className="tw-flex tw-flex-1 tw-flex-col tw-overflow-hidden">
        <>
          <div className="tw-relative tw-flex tw-flex-col tw-pt-7">
            <div className="tw-absolute tw-top-0 tw-z-[200] tw-pl-5 tw-pb-2">
              {t('driverModule.activities-from-tachograph')}
            </div>
            {!loading &&
              (hasNoData || filteredVehicleActivities.length === 0) && (
                <NoDataAvailable
                  message={
                    hasNoData
                      ? t('driverModule.select-driver-and-date')
                      : undefined
                  }
                />
              )}
            {loading ? (
              <LoadingState />
            ) : (
              <Timeline
                key={`details-vehicle-${formatedDate.toISOString()}`}
                {...buildTimelineProps(filteredVehicleActivities, false)}
              />
            )}
          </div>
        </>
      </div>
      <div className="tw-flex tw-flex-1 tw-flex-col tw-overflow-hidden">
        <>
          <div className="tw-relative tw-flex tw-flex-col tw-pt-7">
            <div className="tw-absolute tw-top-0 tw-z-[200] tw-pl-5 tw-pb-2">
              {t('driverModule.activities-modified-by-user')}
            </div>
            {!loading &&
              (hasNoData || filteredModifiedActivities.length === 0) && (
                <NoDataAvailable
                  message={
                    hasNoData
                      ? t('driverModule.select-driver-and-date')
                      : undefined
                  }
                />
              )}
            {loading ? (
              <LoadingState />
            ) : (
              <Timeline
                key={`details-modified-${formatedDate.toISOString()}`}
                {...buildTimelineProps(filteredModifiedActivities, false)}
              />
            )}
          </div>
        </>
      </div>
      <div className="tw-flex tw-flex-1 tw-flex-col tw-overflow-hidden">
        <div className="tw-relative tw-flex tw-flex-col tw-pt-7 tw-gap-4">
          <div className="tw-absolute tw-top-0 tw-z-[200] tw-pl-5 ">
            {t('driverModule.combined-activities')}
          </div>
          {!loading && (hasNoData || filteredActivities.length === 0) && (
            <NoDataAvailable
              message={
                hasNoData ? t('driverModule.select-driver-and-date') : undefined
              }
            />
          )}
          {loading ? (
            <LoadingState />
          ) : (
            <Timeline
              key={`details-combined-${formatedDate.toISOString()}`}
              {...buildTimelineProps(filteredActivities, true)}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default DetailsChartWrapper
